import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection } from "rxdb-hooks";

export const RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME =
  "epocus-encounter-patient";

export const useRxCollectionEpocusEncounterPatient =
  (): RxCollection<EpocusEncounterPatient> | null =>
    useRxCollection<EpocusEncounterPatient>(
      RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME,
    );

export const RELATIONSHIP_TO_HOLDER_OPTIONS = [
  "SELF",
  "SPOUSE",
  "CHILD",
  "OTHER",
] as const;
export type RelationshipToHolder =
  (typeof RELATIONSHIP_TO_HOLDER_OPTIONS)[number];

const EPOCUS_ENCOUNTER_PATIENT_SCHEMA_LITERAL = {
  title: "epocus encounter patient schema",
  version: 3,
  description:
    "Local-only patient details for an encounter that has no linked patient record. Stores name and insurance billing information. Does not replicate.",
  type: "object",
  // encounterId is the primary key — exactly one patient record per encounter
  primaryKey: "encounterId",
  properties: {
    encounterId: {
      type: "string",
      maxLength: 100,
    },
    firstName: {
      type: "string",
    },
    lastName: {
      type: "string",
    },
    insuranceProviderName: {
      type: "string",
    },
    policyNumber: {
      type: "string",
    },
    groupNumber: {
      type: ["string", "null"],
    },
    policyholderName: {
      type: ["string", "null"],
    },
    relationshipToHolder: {
      type: ["string", "null"],
      enum: ["SELF", "SPOUSE", "CHILD", "OTHER", null],
    },
    dob: {
      type: ["string", "null"],
    },
    // Patient sex, prefilled from the DICOM PatientSex tag.
    gender: {
      type: ["string", "null"],
    },
  },
  required: [
    "encounterId",
    "firstName",
    "lastName",
    "insuranceProviderName",
    "policyNumber",
    "relationshipToHolder",
  ],
  encrypted: [
    "firstName",
    "lastName",
    "insuranceProviderName",
    "policyNumber",
    "groupNumber",
    "policyholderName",
    "relationshipToHolder",
    "dob",
    "gender",
  ],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(
  EPOCUS_ENCOUNTER_PATIENT_SCHEMA_LITERAL,
);

export type EpocusEncounterPatient = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_ENCOUNTER_PATIENT_SCHEMA: RxJsonSchema<EpocusEncounterPatient> =
  EPOCUS_ENCOUNTER_PATIENT_SCHEMA_LITERAL;
